import { useState, useEffect } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogOut, AlertCircle } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('user' | 'client_admin' | 'exec' | 'admin')[];
  redirectTo?: string;
  requireOnboarding?: boolean;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  redirectTo = '/login',
  requireOnboarding = true,
}: ProtectedRouteProps) {
  const { user, membership, onboardingCompleted, needsPasswordSetup, loading, signOut } = useAuth();
  const location = useLocation();
  const [membershipTimedOut, setMembershipTimedOut] = useState(false);

  // Timeout for membership loading — if user exists but no membership after 4s, show error
  useEffect(() => {
    if (!user || membership || loading) {
      setMembershipTimedOut(false);
      return;
    }

    const timer = setTimeout(() => {
      setMembershipTimedOut(true);
    }, 4000);

    return () => clearTimeout(timer);
  }, [user, membership, loading]);

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Only block on membership if we need to check roles or onboarding
  // For routes like /onboarding where requireOnboarding=false and no allowedRoles, allow through
  if (!membership && (allowedRoles || requireOnboarding)) {
    if (membershipTimedOut) {
      return (
        <div className="min-h-screen cosmic-bg flex items-center justify-center p-6">
          <Card className="w-full max-w-md bg-card/80 backdrop-blur-sm border-border/50">
            <CardHeader className="text-center">
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle className="text-xl">No Organization Found</CardTitle>
              <CardDescription>
                Your account isn't linked to an organization. Please sign up with an invite code or contact your admin.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button asChild variant="default" className="w-full">
                <Link to="/signup">Sign up with invite code</Link>
              </Button>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => signOut()}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Force users who need password setup to /onboarding (Step 0)
  if (needsPasswordSetup === true && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Check if onboarding is required and not completed
  if (requireOnboarding && onboardingCompleted === false && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Check role-based access - always redirect unauthorized users to /profile
  if (allowedRoles && !allowedRoles.includes(membership.role)) {
    return <Navigate to="/profile" replace />;
  }

  return <>{children}</>;
}
